import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaChevronLeft, FaChevronRight, FaSave } from 'react-icons/fa';
import TextEditor from './components/TextEditor';
import TipsEditor from './components/TipsEditor';
import CausesEditor from './components/CausesEditor';
import AdditionalSectionsEditor from './components/AdditionalSectionsEditor';
import VizAppEditor from './components/VizAppEditor';
// We'll import other editors later

const SECTIONS = [
    { id: 'text', label: 'Basic Text', icon: '📝' },
    { id: 'dynamicText', label: 'Dynamic Text', icon: '💬' },
    { id: 'tips', label: 'Health Tips', icon: '💡' },
    { id: 'causes', label: 'Causes', icon: '⚠️' },
    { id: 'additionalSections', label: 'Additional Sections', icon: '➕' },
    { id: 'vizapp', label: 'VizApp Content', icon: '📱' }
];

const EditorWorkspace = ({
    parameterContent,
    activeParameter,
    activeSection,
    onChangeSection,
    onChangeParameter,
    onBack,
    onUpdateContent,
    onSave
}) => {
    const parameters = Object.keys(parameterContent || {}).sort();
    const currentParamIndex = parameters.indexOf(activeParameter);
    const [activeLang, setActiveLang] = useState('en');

    // Derived State
    const activeData = parameterContent?.[activeParameter] || {};

    // Handlers
    const handleNextParameter = () => {
        if (currentParamIndex < parameters.length - 1) {
            onChangeParameter(parameters[currentParamIndex + 1]);
        }
    };

    const handlePrevParameter = () => {
        if (currentParamIndex > 0) {
            onChangeParameter(parameters[currentParamIndex - 1]);
        }
    };

    const handleSectionChange = (e) => {
        onChangeSection(e.target.value);
    };

    const handleParameterChange = (e) => {
        onChangeParameter(e.target.value);
    };

    // Generic Update Handler (passed down to specific editors)
    const handleDataUpdate = (sectionKey, updatedSectionData) => {
        const newContent = {
            ...parameterContent,
            [activeParameter]: {
                ...parameterContent[activeParameter],
                [sectionKey]: updatedSectionData
            }
        };
        onUpdateContent(newContent);
    };

    // Render the correct sub-editor
    // Pass activeLang and setActiveLang to all editors to sync state
    const renderEditor = () => {
        const commonProps = {
            parameterName: activeParameter,
            activeLang: activeLang,
            onLangChange: setActiveLang
        };

        switch (activeSection) {
            case 'text':
                return <TextEditor
                    data={activeData.text || {}}
                    onUpdate={(newData) => handleDataUpdate('text', newData)}
                    {...commonProps}
                />;
            case 'dynamicText':
                return <TextEditor
                    data={activeData.dynamicText || {}}
                    onUpdate={(newData) => handleDataUpdate('dynamicText', newData)}
                    title="Dynamic Text"
                    {...commonProps}
                />;
            case 'tips':
                return <TipsEditor
                    data={activeData.tips || {}}
                    onUpdate={(newData) => handleDataUpdate('tips', newData)}
                    {...commonProps}
                />;
            case 'causes':
                return <CausesEditor
                    data={activeData.causes || {}}
                    onUpdate={(newData) => handleDataUpdate('causes', newData)}
                    {...commonProps}
                />;
            case 'additionalSections':
                return <AdditionalSectionsEditor
                    data={activeData.additionalSections || []}
                    onUpdate={(newData) => handleDataUpdate('additionalSections', newData)}
                    {...commonProps}
                />;
            case 'vizapp':
                return <VizAppEditor
                    data={activeData.vizapp || {}}
                    onUpdate={(newData) => handleDataUpdate('vizapp', newData)}
                    {...commonProps}
                />;
            default:
                return <div className="pcr-placeholder">Editor for <strong>{activeSection}</strong> is coming soon.</div>;
        }
    };

    // Render Preview
    const renderPreview = () => {
        const langLabel = { en: '(English)', hi: '(Hindi)', ar: '(Arabic)', cz: '(Czech)' }[activeLang];

        const renderSectionHeader = (title) => (
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.25rem' }}>
                {title}
            </div>
        );

        // Ensure we gracefully handle "empty" placeholders for the currently active tab if needed,
        // but otherwise just show all available content in the correct order.
        return (
            <div className="pcr-preview-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', marginBottom: '1rem' }}>
                    <h4 style={{ color: '#334155', margin: 0 }}>{activeParameter}</h4>
                    <span style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase' }}>{langLabel}</span>
                </div>

                {/* 1. TEXT */}
                {(activeData.text?.[activeLang] || activeSection === 'text') && (
                    <div style={{ marginBottom: '1.5rem' }}>
                        {renderSectionHeader('Text')}
                        <p className="pcr-preview-text" style={{ margin: 0 }}>
                            {activeData.text?.[activeLang] ? activeData.text[activeLang] : (activeSection === 'text' && <i style={{ color: '#ccc' }}>No text content for {activeLang}...</i>)}
                        </p>
                    </div>
                )}

                {/* 2. DYNAMIC TEXT */}
                {(activeData.dynamicText?.[activeLang] || activeSection === 'dynamicText') && (
                    <div style={{ marginBottom: '1.5rem' }}>
                        {renderSectionHeader('Dynamic Text')}
                        <p className="pcr-preview-text" style={{ margin: 0 }}>
                            {activeData.dynamicText?.[activeLang] ? activeData.dynamicText[activeLang] : (activeSection === 'dynamicText' && <i style={{ color: '#ccc' }}>No dynamic text content for {activeLang}...</i>)}
                        </p>
                    </div>
                )}

                {/* 3. CAUSES */}
                <div className="pcr-preview-section" style={{ display: (Object.keys(activeData.causes || {}).length > 0 || activeSection === 'causes') ? 'block' : 'none', marginBottom: '1.5rem' }}>
                    {renderSectionHeader('Causes')}
                    {Object.keys(activeData.causes || {}).map(causeType => {
                        const cause = activeData.causes[causeType];
                        if (!cause || typeof cause !== 'object') return null;

                        const hasData = cause.heading?.[activeLang] || cause.description?.[activeLang] || (Array.isArray(cause.list) && cause.list.some(item => item.text?.[activeLang] && item.text[activeLang].trim() !== ''));

                        return (
                            <div key={causeType} style={{ marginBottom: '1rem' }}>
                                <span className={`pcr-badge ${causeType.toLowerCase().includes('high') ? 'danger' : 'warning'}`}>
                                    {causeType.toUpperCase()}
                                </span>
                                {cause.heading?.[activeLang] && <h6 className="pcr-preview-subheading" style={{ marginTop: '0.5rem' }}>{cause.heading[activeLang]}</h6>}
                                {cause.description?.[activeLang] && <p className="pcr-preview-text-sm">{cause.description[activeLang]}</p>}
                                {Array.isArray(cause.list) && cause.list.length > 0 && (
                                    <ul className="pcr-preview-list">
                                        {cause.list.map((item, idx) => {
                                            if (!item.text?.[activeLang] || item.text[activeLang].trim() === '') return null;
                                            return <li key={idx}>{item.text[activeLang]}</li>;
                                        })}
                                    </ul>
                                )}
                                {!hasData && <i style={{ color: '#ccc', fontSize: '0.85rem', display: 'block', marginTop: '0.25rem' }}>No data</i>}
                            </div>
                        );
                    })}
                    {Object.keys(activeData.causes || {}).length === 0 && activeSection === 'causes' && <i style={{ color: '#ccc' }}>No causes content...</i>}
                </div>

                {/* 4. TIPS */}
                <div className="pcr-preview-section" style={{ display: ((activeData.tips?.tipsHeader?.[activeLang] || (Array.isArray(activeData.tips?.tipsSvgText?.[activeLang]) && activeData.tips.tipsSvgText[activeLang].some(t => typeof t === 'string' && t.trim()))) || activeSection === 'tips') ? 'block' : 'none', marginBottom: '1.5rem' }}>
                    {renderSectionHeader('Tips')}
                    {activeData.tips?.tipsHeader?.[activeLang] && <h5 className="pcr-preview-heading">{activeData.tips.tipsHeader[activeLang]}</h5>}
                    {Array.isArray(activeData.tips?.tipsSvgText?.[activeLang]) && (
                        <ul className="pcr-preview-list">
                            {activeData.tips.tipsSvgText[activeLang].map((tip, idx) => {
                                if (typeof tip !== 'string' || tip.trim() === '') return null;
                                return (
                                    <li key={idx}>
                                        {tip}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {(!activeData.tips?.tipsHeader?.[activeLang] && (!Array.isArray(activeData.tips?.tipsSvgText?.[activeLang]) || !activeData.tips.tipsSvgText[activeLang].some(t => typeof t === 'string' && t.trim()))) && activeSection === 'tips' && (
                        <i style={{ color: '#ccc' }}>No tips for {activeLang}...</i>
                    )}
                </div>

                {/* 5. ADDITIONAL SECTIONS */}
                <div className="pcr-preview-section" style={{ display: ((activeData.additionalSections && activeData.additionalSections.length > 0) || activeSection === 'additionalSections') ? 'block' : 'none', marginBottom: '1.5rem' }}>
                    {renderSectionHeader('Additional Sections')}
                    {(activeData.additionalSections || []).map((sec, idx) => (
                        <div key={idx} style={{ background: '#f8fafc', padding: '0.5rem', marginBottom: '0.5rem', borderRadius: '6px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.25rem' }}>
                                {sec.heading?.[activeLang] ? (
                                    <strong style={{ flex: 1 }}>{sec.heading[activeLang]}</strong>
                                ) : <div style={{ flex: 1 }} />}
                                
                                {sec.valueDependent === 'Yes' && sec.valueType && (
                                    <span className={`pcr-badge ${sec.valueType.toLowerCase().includes('high') ? 'danger' : sec.valueType.toLowerCase().includes('low') ? 'warning' : ''}`} style={{ marginLeft: '0.5rem', whiteSpace: 'nowrap' }}>
                                        {sec.valueType}
                                    </span>
                                )}
                            </div>
                            {sec.description?.[activeLang] && <p className="pcr-preview-text-sm">{sec.description[activeLang]}</p>}
                            {sec.list && (
                                <ul className="pcr-preview-list">
                                    {sec.list.map((item, i) => {
                                        if (!item.text?.[activeLang] || item.text[activeLang].trim() === '') return null;
                                        return <li key={i}>{item.text[activeLang]}</li>;
                                    })}
                                </ul>
                            )}
                        </div>
                    ))}
                    {(!activeData.additionalSections || activeData.additionalSections.length === 0) && activeSection === 'additionalSections' && <i style={{ color: '#ccc' }}>No additional sections...</i>}
                </div>

                {/* 6. VizApp */}
                <div className="pcr-preview-section" style={{ display: (Object.keys(activeData.vizapp || {}).length > 0 || activeSection === 'vizapp') ? 'block' : 'none', marginBottom: '1.5rem' }}>
                    {renderSectionHeader('VizApp')}
                    {Object.keys(activeData.vizapp || {}).map(cat => (
                        <div key={cat} style={{ marginBottom: '1rem' }}>
                            <strong>{cat}</strong>
                            <ul className="pcr-preview-list">
                                {(Array.isArray(activeData.vizapp[cat]) ? activeData.vizapp[cat] : []).map((t, i) => {
                                    if (!t.text?.[activeLang] || t.text[activeLang].trim() === '') return null;
                                    return <li key={i}>{t.text[activeLang]}</li>;
                                })}
                            </ul>
                        </div>
                    ))}
                    {Object.keys(activeData.vizapp || {}).length === 0 && activeSection === 'vizapp' && <i style={{ color: '#ccc' }}>No VizApp content...</i>}
                </div>
            </div>
        );
    };

    return (
        <div className="pcr-workspace">
            {/* 1. Navigation Bar */}
            <div className="pcr-nav-bar">
                <div className="pcr-nav-controls">
                    <button className="pcr-nav-btn" onClick={onBack} title="Back to Dashboard">
                        <FaArrowLeft />
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <button className="pcr-nav-btn" disabled={currentParamIndex <= 0} onClick={handlePrevParameter}>
                            <FaChevronLeft />
                        </button>
                        <select className="pcr-nav-select" value={activeParameter} onChange={handleParameterChange}>
                            {parameters.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <button className="pcr-nav-btn" disabled={currentParamIndex >= parameters.length - 1} onClick={handleNextParameter}>
                            <FaChevronRight />
                        </button>
                    </div>

                    <span style={{ color: '#e2e8f0' }}>|</span>

                    <select className="pcr-nav-select" value={activeSection} onChange={handleSectionChange}>
                        {SECTIONS.map(s => <option key={s.id} value={s.id}>{s.icon} {s.label}</option>)}
                    </select>
                </div>

                <div className="pcr-nav-actions">
                    <button
                        className="rc-btn rc-btn-primary"
                        style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                        onClick={() => onSave()}
                    >
                        <FaSave style={{ marginRight: '6px' }} /> Save Changes
                    </button>
                </div>
            </div>

            {/* 2. Split View */}
            <div className="pcr-split-view">
                {/* Left: Input */}
                <div className="pcr-left-col">
                    {renderEditor()}
                </div>

                {/* Right: Preview */}
                <div className="pcr-right-col">
                    <div style={{
                        width: '100%',
                        maxWidth: '375px', // Standard mobile width
                        background: 'white',
                        height: '100%', // Match container height instead of fixed 600px
                        maxHeight: '600px', // Cap it
                        overflowY: 'auto',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        borderRadius: '24px',
                        padding: '1.5rem',
                        border: '8px solid #1e293b'
                    }}>
                        <div style={{ textAlign: 'center', marginBottom: '1rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.5rem' }}>
                            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Live Preview</span>
                        </div>
                        {renderPreview()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditorWorkspace;
